import { useEditor, useNode } from '@craftjs/core';
import { ContentBlockLabel } from '../canvas/ContentBlockLabel/ContentBlockLabel';
import {
  CONTENT_BLOCK_NAME,
  ICON_NODE_NAME,
  IMAGE_NODE_NAME,
  FORM_NODE_NAME,
  FORM_TEXT_FIELD_NODE_NAME,
  FORM_EMAIL_FIELD_NODE_NAME,
  FORM_PHONE_FIELD_NODE_NAME,
  FORM_CONSENT_FIELD_NODE_NAME,
  FORM_DROPDOWN_FIELD_NODE_NAME,
  FORM_SELECTION_FIELD_NODE_NAME,
  TEXT_NODE_NAME,
  BUTTON_NODE_NAME,
  POPUP_NODE_NAME,
} from '@/utils/constants';
import IconImage from '@/assets/icons/icon-image.svg?react';
import IconTextSquare from '@/assets/icons/icon-text-square.svg?react';
import IconBorderAll from '@/assets/icons/icon-border-all.svg?react';
import IconFilterMailSquare from '@/assets/icons/icon-filter-mail-square.svg?react';
import IconMail from '@/assets/icons/icon-mail.svg?react';
import IconCall from '@/assets/icons/icon-call.svg?react';
import IconCheckmarkSquare from '@/assets/icons/icon-checkmark-square.svg?react';
import IconLeftToRightListBullet from '@/assets/icons/icon-left-to-right-list-bullet.svg?react';
import IconCheckList from '@/assets/icons/icon-check-list.svg?react';
import IconStars from '@/assets/icons/icon-stars.svg?react';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import type { ContainerProps } from '@builder4/shared/components/Container';

export const CraftNodeTitle = ({
  className,
  editTitle,
  closeEditMode,
  breakpointCount,
}: {
  className?: string;
  editTitle: boolean;
  closeEditMode: () => void;
  breakpointCount: number;
}) => {
  const { id } = useNode();
  const { setFlexDirection } = useContainerHelper();
  const { isActive } = useEditor((_, query) => ({
    isActive: query.getEvent('selected').contains(id),
  }));
  const { isContentSection, nodeTitle, nodeName, nodeProps } = useNode((node) => ({
    nodeTitle: node.data.custom.displayName || node.data.displayName,
    nodeName: node.data.displayName,
    isContentSection: node.data.displayName === CONTENT_BLOCK_NAME,
    nodeProps: node.data.props as Partial<ContainerProps>,
  }));

  const { getBPProp } = useBreakpointAwareProp(nodeName);
  const flexDirection = getBPProp(nodeProps, 'flexDirection');
  const isFlexRow = flexDirection === 'row';

  const handleToggleDirection = (value: 'row' | 'column') => {
    setFlexDirection(id, value);
  };

  const getIcon = (nodeName: string) => {
    switch (nodeName) {
      case IMAGE_NODE_NAME:
        return IconImage;
      case TEXT_NODE_NAME:
        return IconTextSquare;
      case CONTENT_BLOCK_NAME:
        return IconBorderAll;
      case FORM_NODE_NAME:
        return IconFilterMailSquare;
      case FORM_TEXT_FIELD_NODE_NAME:
        return IconTextSquare;
      case FORM_EMAIL_FIELD_NODE_NAME:
        return IconMail;
      case FORM_PHONE_FIELD_NODE_NAME:
        return IconCall;
      case FORM_CONSENT_FIELD_NODE_NAME:
        return IconCheckmarkSquare;
      case FORM_DROPDOWN_FIELD_NODE_NAME:
        return IconLeftToRightListBullet;
      case FORM_SELECTION_FIELD_NODE_NAME:
        return IconCheckList;
      case ICON_NODE_NAME:
        return IconStars;
      case POPUP_NODE_NAME:
        return IconCheckmarkSquare;
      default:
        return undefined;
    }
  };

  const showTitle = breakpointCount < 2;
  const hideIcon = breakpointCount >= 2 && nodeName === BUTTON_NODE_NAME;
  const showIcon = breakpointCount < 6 && !hideIcon;
  const icon = getIcon(nodeName);

  const showSwitcher = breakpointCount < 1;
  return showIcon ? (
    <ContentBlockLabel
      closeEditMode={closeEditMode}
      editTitle={editTitle}
      switcher={isActive && isContentSection && showSwitcher}
      icon={icon}
      onClick={handleToggleDirection}
      align={isFlexRow ? 'horizontal' : 'vertical'}
      text={showTitle ? nodeTitle : ''}
      className={`${className}`}
      id={id}
    />
  ) : null;
};
